import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Box, 
  Typography, 
  Paper, 
  TextField, 
  Button, 
  Divider,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputAdornment
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { checkUserExists } from '../services/firebase';

// List of countries with country codes
const countries = [
  { name: 'India', code: '+91' },
  { name: 'United States', code: '+1' },
  { name: 'United Kingdom', code: '+44' },
  { name: 'Canada', code: '+1' },
  { name: 'Australia', code: '+61' },
  { name: 'Brazil', code: '+55' },
  { name: 'China', code: '+86' },
  { name: 'France', code: '+33' },
  { name: 'Germany', code: '+49' },
  { name: 'Japan', code: '+81' },
  { name: 'Russia', code: '+7' },
  { name: 'South Africa', code: '+27' },
  { name: 'Spain', code: '+34' },
  { name: 'Italy', code: '+39' },
  { name: 'Mexico', code: '+52' },
  { name: 'Netherlands', code: '+31' },
  { name: 'Singapore', code: '+65' },
  { name: 'Sweden', code: '+46' },
  { name: 'Switzerland', code: '+41' },
  { name: 'United Arab Emirates', code: '+971' },
].sort((a, b) => a.name.localeCompare(b.name));

interface LocationState {
  email?: string;
}

const SignupPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as LocationState;
  
  // State for user information
  const [email, setEmail] = useState<string>(locationState?.email || '');
  const [name, setName] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>('');
  const [country, setCountry] = useState<string>('India');
  const [countryCode, setCountryCode] = useState<string>('+91');
  
  // State for UI
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [fieldErrors, setFieldErrors] = useState<{
    name?: string;
    email?: string;
    phoneNumber?: string;
    country?: string;
  }>({});
  
  // Auth context
  const { sendMagicLink } = useAuth();
  
  // Auto-login for test account
  useEffect(() => {
    const autoLogin = async () => {
      // Check if we're auto-logging in from localStorage flag
      const autoLoginFlag = localStorage.getItem('autoLoginTestAccount');
      if (autoLoginFlag === 'true') {
        localStorage.removeItem('autoLoginTestAccount');
        // Redirect to main page - the auth context will handle the actual login
        navigate('/');
      }
    };
    
    autoLogin();
  }, [navigate]);
  
  // Check if user already exists when component mounts
  useEffect(() => {
    const checkExistingUser = async () => {
      if (email && email.toLowerCase() !== 'testervip@ut.com') {
        try {
          const userExists = await checkUserExists(email);
          if (userExists) {
            setError('An account with this email already exists. Please login instead.');
            setTimeout(() => {
              navigate('/login');
            }, 2000);
          }
        } catch (err) {
          console.error('Error checking if user exists:', err);
        }
      }
    };
    
    checkExistingUser();
  }, [email, navigate]);
  
  // Handle email change
  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value);
    setFieldErrors({ ...fieldErrors, email: undefined });
    setError(null);
  };
  
  // Handle name change
  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value);
    setFieldErrors({ ...fieldErrors, name: undefined });
  };
  
  // Handle phone number change
  const handlePhoneNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only numbers
    const value = event.target.value.replace(/[^0-9]/g, '');
    setPhoneNumber(value);
    setFieldErrors({ ...fieldErrors, phoneNumber: undefined });
  };
  
  // Handle country change
  const handleCountryChange = (event: SelectChangeEvent<string>) => {
    const selectedCountry = event.target.value;
    setCountry(selectedCountry);
    
    // Update country code
    const selectedCountryData = countries.find(c => c.name === selectedCountry);
    if (selectedCountryData) {
      setCountryCode(selectedCountryData.code);
    }
    
    setFieldErrors({ ...fieldErrors, country: undefined });
  };
  
  // Validate form
  const validateForm = (): boolean => {
    const errors: {
      name?: string;
      email?: string;
      phoneNumber?: string;
      country?: string;
    } = {};
    
    // Skip validation for test account
    if (email.toLowerCase() === 'testervip@ut.com') {
      return true;
    }
    
    // Validate name
    if (!name.trim()) {
      errors.name = 'Please enter your name';
    }
    
    // Validate email
    if (!email.trim()) {
      errors.email = 'Please enter your email address';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      errors.email = 'Please enter a valid email address';
    }
    
    // Validate phone number
    if (!phoneNumber.trim()) {
      errors.phoneNumber = 'Please enter your phone number';
    } else if (phoneNumber.length < 8) {
      errors.phoneNumber = 'Phone number is too short';
    }
    
    // Validate country
    if (!country) {
      errors.country = 'Please select your country';
    }
    
    setFieldErrors(errors);
    
    return Object.keys(errors).length === 0;
  };
  
  // Handle form submission
  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    
    if (loading) return;
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Special case for Razorpay test account
      if (email.toLowerCase() === 'testervip@ut.com') {
        // Set flag for auto-login and trigger page reload
        localStorage.setItem('testAccountLogin', 'true');
        localStorage.setItem('autoLoginTestAccount', 'true');
        
        // Show success message
        setError(null);
        setLoading(false);
        
        // Show a message and redirect
        setError('Logging in as test account...');
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return;
      }
      
      // Check if user already exists
      const userExists = await checkUserExists(email);
      
      if (userExists) {
        setError('An account with this email already exists. Please login instead.');
        setTimeout(() => {
          navigate('/login');
        }, 2000);
        return;
      }
      
      // Prepare user data
      const userData = {
        name,
        email,
        phoneNumber: `${countryCode}${phoneNumber}`,
        country
      };
      
      // Store user data in localStorage for later use
      localStorage.setItem('userSignupData', JSON.stringify(userData));
      
      // Send magic link
      await sendMagicLink(email);
      setEmailSent(true);
    } catch (err) {
      console.error('Error sending magic link:', err);
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper 
        elevation={0} 
        className="glass-container"
        sx={{ 
          p: 4,
          mb: 4,
          borderRadius: 2
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Typography variant="h4" component="h1" gutterBottom>
            Create Your Account
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Sign up to access premium features and save your capsules
          </Typography>
        </Box>
        
        {emailSent ? (
          <Box sx={{ textAlign: 'center' }}>
            <Alert severity="success" sx={{ mb: 3 }}>
              Magic link sent! Check your email to sign in.
            </Alert>
            
            <Typography variant="body1" paragraph>
              We've sent a sign-in link to <strong>{email}</strong>. Click the link in the email to sign in to your account.
            </Typography>
            
            <Typography variant="body2" color="text.secondary" paragraph>
              The link will expire in 15 minutes. If you don't see the email, check your spam folder.
            </Typography>
            
            <Button
              variant="outlined"
              color="primary"
              onClick={() => setEmailSent(false)}
              sx={{ mt: 2, borderRadius: 4 }}
            >
              Use a different email
            </Button>
          </Box>
        ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Full Name"
              variant="outlined"
              value={name}
              onChange={handleNameChange}
              disabled={loading}
              error={!!fieldErrors.name}
              helperText={fieldErrors.name}
              sx={{ mb: 3 }}
            />
            
            <TextField
              fullWidth
              label="Email Address"
              variant="outlined"
              type="email"
              value={email}
              onChange={handleEmailChange}
              disabled={loading}
              error={!!fieldErrors.email}
              helperText={fieldErrors.email}
              sx={{ mb: 3 }}
            />
            
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel id="country-select-label">Country</InputLabel>
              <Select
                labelId="country-select-label"
                value={country}
                label="Country"
                onChange={handleCountryChange}
                disabled={loading}
                error={!!fieldErrors.country}
              >
                {countries.map((country) => (
                  <MenuItem key={country.name} value={country.name}>
                    {country.name}
                  </MenuItem>
                ))}
              </Select>
              {fieldErrors.country && (
                <Typography color="error" variant="caption" sx={{ mt: 0.5, ml: 1.5 }}>
                  {fieldErrors.country}
                </Typography>
              )}
            </FormControl>
            
            <TextField
              fullWidth
              label="Phone Number"
              variant="outlined"
              value={phoneNumber}
              onChange={handlePhoneNumberChange}
              disabled={loading}
              error={!!fieldErrors.phoneNumber}
              helperText={fieldErrors.phoneNumber}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    {countryCode}
                  </InputAdornment>
                ),
              }}
              sx={{ mb: 3 }}
            />
            
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              fullWidth
              disabled={loading}
              sx={{ 
                py: 1.5,
                borderRadius: 4
              }}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                'Create Account'
              )}
            </Button>
            
            {error && (
              <Alert severity={error.includes('Logging in as test account') ? 'success' : 'error'} sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
            
            <Box sx={{ mt: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                We'll email you a magic link for password-free sign in.
              </Typography>
            </Box>
          </form>
        )}
        
        <Divider sx={{ my: 4 }} />
        
        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">
            Already have an account?
          </Typography>
          
          <Button
            component={Link}
            to="/login"
            variant="outlined"
            color="primary"
            sx={{ mt: 2, borderRadius: 4, px: 4 }}
          >
            Login
          </Button>
        </Box>
      </Paper>
    </Container>
  );
};

export default SignupPage;
